// src/components/aols/OLSstudent/CountriesIndicator.tsx
import React, { useState } from "react";
import { Table, Typography, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { debounce } from "lodash";
import { LocationFee, LocationFactor } from "./types";
import "./LocationIndicator.css";

const { Text } = Typography;

interface CountriesIndicatorProps {
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
}

const CountriesIndicator: React.FC<CountriesIndicatorProps> = ({ searchQuery, onSearchQueryChange }) => {
  // Countries data
  const countries: LocationFee[] = [
    {
      location: "France",
      fee: "$7,500",
      gdpIndicator: "$3,040B",
      gdpStatus: "High",
      globalStatus: "Top Tier",
      globalStatusIndicator: "5/5",
      statusBars: 2,
      population: "67.97M",
      country: "France",
    },
    {
      location: "Italy",
      fee: "$7,500",
      gdpIndicator: "$2,190B",
      gdpStatus: "High",
      globalStatus: "Advanced",
      globalStatusIndicator: "4.5/5",
      statusBars: 2,
      population: "58.87M",
      country: "Italy",
    },
    {
      location: "Brazil",
      fee: "$12,000",
      gdpIndicator: "$2,170B",
      gdpStatus: "Medium",
      globalStatus: "Emerging",
      globalStatusIndicator: "3.5/5",
      statusBars: 1,
      population: "216.42M",
      country: "Brazil",
    },
    {
      location: "Canada",
      fee: "$7,300",
      gdpIndicator: "$2,110B",
      gdpStatus: "High",
      globalStatus: "Top Tier",
      globalStatusIndicator: "5/5",
      statusBars: 2,
      population: "38.78M",
      country: "Canada",
    },
    {
      location: "Russia",
      fee: "$70,000",
      gdpIndicator: "$1,860B",
      gdpStatus: "Medium",
      globalStatus: "Sanction",
      globalStatusIndicator: "3.5/5",
      statusBars: 1,
      population: "144.44M",
      country: "Russia",
    },
    {
      location: "Mexico",
      fee: "$9,000",
      gdpIndicator: "$1,810B",
      gdpStatus: "Medium",
      globalStatus: "Emerging",
      globalStatusIndicator: "3.5/5",
      statusBars: 1,
      population: "128.46M",
      country: "Mexico",
    },
    {
      location: "South Korea",
      fee: "$6,900",
      gdpIndicator: "$1,700B",
      gdpStatus: "High",
      globalStatus: "Advanced",
      globalStatusIndicator: "4.5/5",
      statusBars: 2,
      population: "51.78M",
      country: "South Korea",
    },
    {
      location: "Australia",
      fee: "$7,600",
      gdpIndicator: "$1,680B",
      gdpStatus: "High",
      globalStatus: "Top Tier",
      globalStatusIndicator: "5/5",
      statusBars: 2,
      population: "26.64M",
      country: "Australia",
    },
    {
      location: "Spain",
      fee: "$6,800",
      gdpIndicator: "$1,580B",
      gdpStatus: "High",
      globalStatus: "Advanced",
      globalStatusIndicator: "4.5/5",
      statusBars: 2,
      population: "47.52M",
      country: "Spain",
    },
    {
      location: "Indonesia",
      fee: "$10,000",
      gdpIndicator: "$1,410B",
      gdpStatus: "Medium",
      globalStatus: "Emerging",
      globalStatusIndicator: "3.5/5",
      statusBars: 1,
      population: "279.11M",
      country: "Indonesia",
    },
    {
      location: "Turkey",
      fee: "$8,000",
      gdpIndicator: "$1,150B",
      gdpStatus: "Medium",
      globalStatus: "Emerging",
      globalStatusIndicator: "3.5/5",
      statusBars: 1,
      population: "85.82M",
      country: "Turkey",
    },
    {
      location: "Netherlands",
      fee: "$5,000",
      gdpIndicator: "$1,090B",
      gdpStatus: "High",
      globalStatus: "Advanced",
      globalStatusIndicator: "4.5/5",
      statusBars: 2,
      population: "17.81M",
      country: "Netherlands",
    },
    {
      location: "Saudi Arabia",
      fee: "$14,000",
      gdpIndicator: "$1,060B",
      gdpStatus: "High",
      globalStatus: "Top Tier",
      globalStatusIndicator: "5/5",
      statusBars: 2,
      population: "36.95M",
      country: "Saudi Arabia",
    },
    {
      location: "Switzerland",
      fee: "$12,000",
      gdpIndicator: "$905B",
      gdpStatus: "High",
      globalStatus: "Top Tier",
      globalStatusIndicator: "5/5",
      statusBars: 2,
      population: "8.91M",
      country: "Switzerland",
    },
    {
      location: "Poland",
      fee: "$13,000",
      gdpIndicator: "$842B",
      gdpStatus: "Medium",
      globalStatus: "Advanced",
      globalStatusIndicator: "4/5",
      statusBars: 2,
      population: "41.02M",
      country: "Poland",
    },
    {
      location: "Taiwan",
      fee: "TWD 72,000",
      gdpIndicator: "$751B",
      gdpStatus: "Medium",
      globalStatus: "Advanced",
      globalStatusIndicator: "4/5",
      statusBars: 2,
      population: "23.42M",
      country: "Taiwan",
    },
    {
      location: "Belgium",
      fee: "$5,900",
      gdpIndicator: "$627B",
      gdpStatus: "High",
      globalStatus: "Advanced",
      globalStatusIndicator: "4.5/5",
      statusBars: 2,
      population: "11.67M",
      country: "Belgium",
    },
    {
      location: "Argentina",
      fee: "$8,000",
      gdpIndicator: "$621B",
      gdpStatus: "Medium",
      globalStatus: "Emerging",
      globalStatusIndicator: "3.5/5",
      statusBars: 1,
      population: "45.77M",
      country: "Argentina",
    },
    {
      location: "Sweden",
      fee: "$5,800",
      gdpIndicator: "$597B",
      gdpStatus: "High",
      globalStatus: "Advanced",
      globalStatusIndicator: "4.5/5",
      statusBars: 2,
      population: "10.55M",
      country: "Sweden",
    },
    {
      location: "Ireland",
      fee: "$5,300",
      gdpIndicator: "$589B",
      gdpStatus: "High",
      globalStatus: "Top Tier",
      globalStatusIndicator: "5/5",
      statusBars: 2,
      population: "5.15M",
      country: "Ireland",
    },
    {
      location: "Norway",
      fee: "$5,500",
      gdpIndicator: "$546B",
      gdpStatus: "High",
      globalStatus: "Top Tier",
      globalStatusIndicator: "5/5",
      statusBars: 2,
      population: "5.49M",
      country: "Norway",
    },
    {
      location: "Austria",
      fee: "$8,600",
      gdpIndicator: "$526B",
      gdpStatus: "High",
      globalStatus: "Advanced",
      globalStatusIndicator: "4.5/5",
      statusBars: 2,
      population: "8.94M",
      country: "Austria",
    },
    {
      location: "Israel",
      fee: "$9,000",
      gdpIndicator: "$521B",
      gdpStatus: "High",
      globalStatus: "Advanced",
      globalStatusIndicator: "4.5/5",
      statusBars: 2,
      population: "9.82M",
      country: "Israel",
    },
    {
      location: "Thailand",
      fee: "$11,000",
      gdpIndicator: "$512B",
      gdpStatus: "Medium",
      globalStatus: "Emerging",
      globalStatusIndicator: "3.5/5",
      statusBars: 1,
      population: "71.80M",
      country: "Thailand",
    },
    {
      location: "United Arab Emirates",
      fee: "$14,000",
      gdpIndicator: "$509B",
      gdpStatus: "High",
      globalStatus: "Top Tier",
      globalStatusIndicator: "5/5",
      statusBars: 2,
      population: "9.52M",
      country: "United Arab Emirates",
    },
    {
      location: "Singapore",
      fee: "$10,000",
      gdpIndicator: "$497B",
      gdpStatus: "High",
      globalStatus: "Top Tier",
      globalStatusIndicator: "5/5",
      statusBars: 2,
      population: "5.92M",
      country: "Singapore",
    },
    {
      location: "Bangladesh",
      fee: "$5,100",
      gdpIndicator: "$446B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "172.95M",
      country: "Bangladesh",
    },
    {
      location: "Philippines",
      fee: "$6,100",
      gdpIndicator: "$435B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "117.34M",
      country: "Philippines",
    },
    {
      location: "Vietnam",
      fee: "$5,500",
      gdpIndicator: "$433B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "98.86M",
      country: "Vietnam",
    },
    {
      location: "Malaysia",
      fee: "$6,000",
      gdpIndicator: "$430B",
      gdpStatus: "Medium",
      globalStatus: "Emerging",
      globalStatusIndicator: "3.5/5",
      statusBars: 1,
      population: "34.31M",
      country: "Malaysia",
    },
    {
      location: "Denmark",
      fee: "$4,500",
      gdpIndicator: "$420B",
      gdpStatus: "High",
      globalStatus: "Advanced",
      globalStatusIndicator: "4.5/5",
      statusBars: 2,
      population: "5.93M",
      country: "Denmark",
    },
    {
      location: "Egypt",
      fee: "$5,000",
      gdpIndicator: "$398B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "112.72M",
      country: "Egypt",
    },
    {
      location: "Nigeria",
      fee: "$5,000",
      gdpIndicator: "$390B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "223.80M",
      country: "Nigeria",
    },
    {
      location: "Hong Kong",
      fee: "$7,000",
      gdpIndicator: "$385B",
      gdpStatus: "High",
      globalStatus: "Top Tier",
      globalStatusIndicator: "5/5",
      statusBars: 2,
      population: "7.50M",
      country: "Hong Kong",
    },
    {
      location: "South Africa",
      fee: "$8,500",
      gdpIndicator: "$380B",
      gdpStatus: "Medium",
      globalStatus: "Emerging",
      globalStatusIndicator: "3.5/5",
      statusBars: 1,
      population: "60.41M",
      country: "South Africa",
    },
    {
      location: "Iran",
      fee: "$60,000",
      gdpIndicator: "$366B",
      gdpStatus: "Low",
      globalStatus: "Sanction",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "89.17M",
      country: "Iran",
    },
    {
      location: "Colombia",
      fee: "$7,000",
      gdpIndicator: "$363B",
      gdpStatus: "Medium",
      globalStatus: "Emerging",
      globalStatusIndicator: "3.5/5",
      statusBars: 1,
      population: "52.58M",
      country: "Colombia",
    },
    {
      location: "Romania",
      fee: "$5,800",
      gdpIndicator: "$350B",
      gdpStatus: "Medium",
      globalStatus: "Emerging",
      globalStatusIndicator: "3.5/5",
      statusBars: 1,
      population: "19.05M",
      country: "Romania",
    },
    {
      location: "Chile",
      fee: "$6,800",
      gdpIndicator: "$344B",
      gdpStatus: "Medium",
      globalStatus: "Emerging",
      globalStatusIndicator: "3.5/5",
      statusBars: 1,
      population: "19.60M",
      country: "Chile",
    },
    {
      location: "Pakistan",
      fee: "$8,500",
      gdpIndicator: "$340B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "241.49M",
      country: "Pakistan",
    },
    {
      location: "Czech Republic",
      fee: "$5,600",
      gdpIndicator: "$335B",
      gdpStatus: "Medium",
      globalStatus: "Advanced",
      globalStatusIndicator: "4/5",
      statusBars: 2,
      population: "10.88M",
      country: "Czech Republic",
    },
    {
      location: "Finland",
      fee: "$4,000",
      gdpIndicator: "$305B",
      gdpStatus: "High",
      globalStatus: "Advanced",
      globalStatusIndicator: "4.5/5",
      statusBars: 2,
      population: "5.56M",
      country: "Finland",
    },
    {
      location: "Iraq",
      fee: "$7,500",
      gdpIndicator: "$297B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "45.50M",
      country: "Iraq",
    },
    {
      location: "Portugal",
      fee: "$6,100",
      gdpIndicator: "$276B",
      gdpStatus: "Medium",
      globalStatus: "Advanced",
      globalStatusIndicator: "4/5",
      statusBars: 2,
      population: "10.41M",
      country: "Portugal",
    },
    {
      location: "Peru",
      fee: "$6,500",
      gdpIndicator: "$264B",
      gdpStatus: "Medium",
      globalStatus: "Emerging",
      globalStatusIndicator: "3.5/5",
      statusBars: 1,
      population: "34.35M",
      country: "Peru",
    },
    {
      location: "Kazakhstan",
      fee: "$8,500",
      gdpIndicator: "$259B",
      gdpStatus: "Medium",
      globalStatus: "Emerging",
      globalStatusIndicator: "3.5/5",
      statusBars: 1,
      population: "19.77M",
      country: "Kazakhstan",
    },
    {
      location: "New Zealand",
      fee: "$5,800",
      gdpIndicator: "$249B",
      gdpStatus: "High",
      globalStatus: "Advanced",
      globalStatusIndicator: "4.5/5",
      statusBars: 2,
      population: "5.31M",
      country: "New Zealand",
    },
    {
      location: "Greece",
      fee: "$6,700",
      gdpIndicator: "$242B",
      gdpStatus: "Medium",
      globalStatus: "Advanced",
      globalStatusIndicator: "4/5",
      statusBars: 2,
      population: "10.36M",
      country: "Greece",
    },
    {
      location: "Qatar",
      fee: "$8,500",
      gdpIndicator: "$235B",
      gdpStatus: "High",
      globalStatus: "Top Tier",
      globalStatusIndicator: "5/5",
      statusBars: 2,
      population: "2.83M",
      country: "Qatar",
    },
    {
      location: "Algeria",
      fee: "$6,100",
      gdpIndicator: "$224B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "45.61M",
      country: "Algeria",
    },
    {
      location: "Ukraine",
      fee: "UAH 63,000",
      gdpIndicator: "$173B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "36.74M",
      country: "Ukraine",
    },
    {
      location: "Hungary",
      fee: "$26,000",
      gdpIndicator: "$203B",
      gdpStatus: "Medium",
      globalStatus: "Sanction",
      globalStatusIndicator: "4/5",
      statusBars: 2,
      population: "9.67M",
      country: "Hungary",
    },
    {
      location: "Kuwait",
      fee: "$8,400",
      gdpIndicator: "$159B",
      gdpStatus: "High",
      globalStatus: "Top Tier",
      globalStatusIndicator: "5/5",
      statusBars: 2,
      population: "4.31M",
      country: "Kuwait",
    },
    {
      location: "Ethiopia",
      fee: "$7,400",
      gdpIndicator: "$155B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "126.52M",
      country: "Ethiopia",
    },
    {
      location: "Morocco",
      fee: "$6,000",
      gdpIndicator: "$147B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "37.84M",
      country: "Morocco",
    },
    {
      location: "Slovakia",
      fee: "$10,200",
      gdpIndicator: "$133B",
      gdpStatus: "Medium",
      globalStatus: "Advanced",
      globalStatusIndicator: "4/5",
      statusBars: 2,
      population: "5.43M",
      country: "Slovakia",
    },
    {
      location: "Cuba",
      fee: "$7,000",
      gdpIndicator: "$103B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "11.19M",
      country: "Cuba",
    },
    {
      location: "Dominican Republic",
      fee: "$7,000",
      gdpIndicator: "$120B",
      gdpStatus: "Medium",
      globalStatus: "Emerging",
      globalStatusIndicator: "3.5/5",
      statusBars: 1,
      population: "11.33M",
      country: "Dominican Republic",
    },
    {
      location: "Ecuador",
      fee: "$6,800",
      gdpIndicator: "$118B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "18.19M",
      country: "Ecuador",
    },
    {
      location: "Puerto Rico",
      fee: "$7,700",
      gdpIndicator: "$117B",
      gdpStatus: "Medium",
      globalStatus: "Advanced",
      globalStatusIndicator: "4/5",
      statusBars: 2,
      population: "3.22M",
      country: "Puerto Rico",
    },
    {
      location: "Kenya",
      fee: "$5,500",
      gdpIndicator: "$112B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "55.10M",
      country: "Kenya",
    },
    {
      location: "Oman",
      fee: "$7,300",
      gdpIndicator: "$108B",
      gdpStatus: "Medium",
      globalStatus: "Emerging",
      globalStatusIndicator: "3.5/5",
      statusBars: 1,
      population: "4.64M",
      country: "Oman",
    },
    {
      location: "Bulgaria",
      fee: "$6,000",
      gdpIndicator: "$103B",
      gdpStatus: "Medium",
      globalStatus: "Emerging",
      globalStatusIndicator: "3.5/5",
      statusBars: 1,
      population: "6.45M",
      country: "Bulgaria",
    },
    {
      location: "Guatemala",
      fee: "$6,000",
      gdpIndicator: "$102B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "18.21M",
      country: "Guatemala",
    },
    {
      location: "Angola",
      fee: "$5,000",
      gdpIndicator: "$93B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "36.68M",
      country: "Angola",
    },
    {
      location: "Venezuela",
      fee: "$9,000",
      gdpIndicator: "$92B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "28.30M",
      country: "Venezuela",
    },
    {
      location: "Uzbekistan",
      fee: "$11,000",
      gdpIndicator: "$90B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "35.16M",
      country: "Uzbekistan",
    },
    {
      location: "Luxembourg",
      fee: "$10,000",
      gdpIndicator: "$89B",
      gdpStatus: "High",
      globalStatus: "Top Tier",
      globalStatusIndicator: "5/5",
      statusBars: 2,
      population: "0.66M",
      country: "Luxembourg",
    },
    {
      location: "Costa Rica",
      fee: "$6,000",
      gdpIndicator: "$85B",
      gdpStatus: "Medium",
      globalStatus: "Emerging",
      globalStatusIndicator: "3.5/5",
      statusBars: 1,
      population: "5.21M",
      country: "Costa Rica",
    },
    {
      location: "Tanzania",
      fee: "$5,500",
      gdpIndicator: "$84B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "67.44M",
      country: "Tanzania",
    },
    {
      location: "Panama",
      fee: "$7,000",
      gdpIndicator: "$82B",
      gdpStatus: "Medium",
      globalStatus: "Emerging",
      globalStatusIndicator: "3.5/5",
      statusBars: 1,
      population: "4.47M",
      country: "Panama",
    },
    {
      location: "Turkmenistan",
      fee: "$6,000",
      gdpIndicator: "$81B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "6.52M",
      country: "Turkmenistan",
    },
    {
      location: "Croatia",
      fee: "$6,400",
      gdpIndicator: "$80B",
      gdpStatus: "Medium",
      globalStatus: "Advanced",
      globalStatusIndicator: "4/5",
      statusBars: 2,
      population: "3.85M",
      country: "Croatia",
    },
    {
      location: "Ivory Coast",
      fee: "$5,300",
      gdpIndicator: "$79B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "29.34M",
      country: "Ivory Coast",
    },
    {
      location: "Lithuania",
      fee: "$5,200",
      gdpIndicator: "$79B",
      gdpStatus: "Medium",
      globalStatus: "Advanced",
      globalStatusIndicator: "4/5",
      statusBars: 2,
      population: "2.83M",
      country: "Lithuania",
    },
    {
      location: "Azerbaijan",
      fee: "$5,100",
      gdpIndicator: "$79B",
      gdpStatus: "Low",
      globalStatus: "Emerging",
      globalStatusIndicator: "2.5/5",
      statusBars: 1,
      population: "10.14M",
      country: "Azerbaijan",
    },
    {
      location: "China",
      fee: "$28,000",
      gdpIndicator: "$14T",
      gdpStatus: "High",
      globalStatus: "Sanction",
      globalStatusIndicator: "5/5",
      statusBars: 2,
      population: "1,410M",
      country: "China",
    },
    {
      location: "Japan",
      fee: "$8,000",
      gdpIndicator: "$5T",
      gdpStatus: "High",
      globalStatus: "Top Tier",
      globalStatusIndicator: "5/5",
      statusBars: 2,
      population: "125.12M",
      country: "Japan",
    },
    {
      location: "India",
      fee: "$14,000",
      gdpIndicator: "$2.9T",
      gdpStatus: "Medium",
      globalStatus: "Emerging",
      globalStatusIndicator: "3/5",
      statusBars: 1,
      population: "1,441M",
      country: "India",
    },
    {
      location: "UK",
      fee: "$7,500",
      gdpIndicator: "$2.7T",
      gdpStatus: "High",
      globalStatus: "Advanced",
      globalStatusIndicator: "4.5/5",
      statusBars: 2,
      population: "67.85M",
      country: "UK",
    },
    {
      location: "Deutschland",
      fee: "$11,000",
      gdpIndicator: "$3.8T",
      gdpStatus: "High",
      globalStatus: "Top Tier",
      globalStatusIndicator: "5/5",
      statusBars: 2,
      population: "84.36M",
      country: "Deutschland",
    },
  ];

  // Debounce the search input to prevent excessive re-renders
  const debouncedSetSearchQuery = debounce((value: string) => {
    onSearchQueryChange(value);
  }, 300);

  // Clean up debounce on component unmount
  React.useEffect(() => {
    return () => {
      debouncedSetSearchQuery.cancel();
    };
  }, []);

  // Filter locations based on search query
  const filteredLocations = React.useMemo(() => {
    return countries.filter((entry) => {
      if (!searchQuery) return false; // Hide all locations if no search query
      const query = searchQuery.toLowerCase();
      return (
        entry.location.toLowerCase().includes(query) ||
        (entry.country && entry.country.toLowerCase().includes(query))
      );
    });
  }, [searchQuery, countries]);

  return (
    <>
      <div className="search-container">
        <span className="prompt-text">
          Search for a country to view tuition fees:
        </span>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Input
            className="styled-input"
            prefix={<SearchOutlined />}
            placeholder="e.g., Japan, Brazil"
            value={searchQuery}
            onChange={(e) => debouncedSetSearchQuery(e.target.value)}
            aria-label="Search for a country"
          />
          {searchQuery && (
            <button
              className="clear-button"
              onClick={() => onSearchQueryChange("")}
              aria-label="Clear search query"
            >
              Clear Search
            </button>
          )}
        </div>
      </div>
      {searchQuery && filteredLocations.length > 0 ? (
        <Table
          columns={[
            {
              title: "Location",
              dataIndex: "location",
              key: "location",
              render: (text: string, record: LocationFee) => (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div className="status-bars" data-bars={record.statusBars}></div>
                  <span>{text}</span>
                </div>
              ),
            },
            {
              title: "GDP Indicator",
              dataIndex: "gdpIndicator",
              key: "gdpIndicator",
              render: (text: string, record: LocationFee) => (
                <div>
                  <div style={{ fontSize: "16px", fontWeight: 600 }}>{text}</div>
                  <div style={{ fontSize: "12px", color: "#ccc" }}>{record.gdpStatus}</div>
                </div>
              ),
            },
            {
              title: "Population",
              dataIndex: "population",
              key: "population",
              render: (text: string) => (
                <div style={{ fontSize: "16px", fontWeight: 600 }}>{text || "N/A"}</div>
              ),
            },
            {
              title: "Global Status",
              dataIndex: "globalStatus",
              key: "globalStatus",
              render: (text: string, record: LocationFee) => (
                <div>
                  <div style={{ fontSize: "16px", fontWeight: 600 }}>{text}</div>
                  <div style={{ fontSize: "12px", color: "#ccc" }}>{record.globalStatusIndicator}</div>
                </div>
              ),
            },
            { title: "General Fee", dataIndex: "fee", key: "fee" },
          ]}
          dataSource={filteredLocations}
          pagination={false}
          rowKey={(record) => record.location}
          rowClassName={(record) => (record.isChild ? "child-row" : "")}
        />
      ) : searchQuery ? (
        <Text style={{ color: "#ffffff" }}>No countries found for "{searchQuery}".</Text>
      ) : null}
    </>
  );
};

export default CountriesIndicator;